import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../client'
import type { DiaNaoUtil, ItemNaAdministracao, ListaDeApoio } from '../../types/usuario'

/**
 * As chaves da administração de listas de apoio.
 *
 * Prefixo comum e invalidação grossa: cadastrar um sistema muda a lista da administração E a
 * lista de escolha do assistente de nova solicitação. Cache fino aqui economizaria uma requisição
 * e custaria um formulário sem a opção que a pessoa acabou de criar.
 */
const chaves = {
  tudo: () => ['admin', 'referencias'] as const,
  lista: (qual: ListaDeApoio) => ['admin', 'referencias', qual] as const,
  dias: () => ['admin', 'referencias', 'dias-nao-uteis'] as const,
}

/**
 * Os itens de uma lista, inclusive os desativados.
 *
 * @param qual sistemas ou finalidades
 * @returns a consulta
 */
export function useItensDaLista(qual: ListaDeApoio) {
  return useQuery({
    queryKey: chaves.lista(qual),
    queryFn: async () => (await api.get<ItemNaAdministracao[]>(`/admin/referencias/${qual}`)).data,
  })
}

/**
 * Cadastra um item.
 *
 * @param qual sistemas ou finalidades
 * @returns a mutação
 */
export function useCriarItem(qual: ListaDeApoio) {
  return useMutacaoDeReferencia((corpo: { codigo: string; rotulo: string }) =>
    api.post(`/admin/referencias/${qual}`, corpo),
  )
}

/**
 * Renomeia um item. O código não muda.
 *
 * @param qual sistemas ou finalidades
 * @returns a mutação
 */
export function useRenomearItem(qual: ListaDeApoio) {
  return useMutacaoDeReferencia((corpo: { id: number; rotulo: string }) =>
    api.put(`/admin/referencias/${qual}/${corpo.id}`, { rotulo: corpo.rotulo }),
  )
}

/**
 * Ativa ou desativa um item.
 *
 * @param qual sistemas ou finalidades
 * @returns a mutação
 */
export function useDefinirItemAtivo(qual: ListaDeApoio) {
  return useMutacaoDeReferencia((corpo: { id: number; ativo: boolean }) =>
    api.patch(`/admin/referencias/${qual}/${corpo.id}/ativo`, { ativo: corpo.ativo }),
  )
}

/** @returns os dias em que o relógio do prazo não anda */
export function useDiasNaoUteis() {
  return useQuery({
    queryKey: chaves.dias(),
    queryFn: async () => (await api.get<DiaNaoUtil[]>('/admin/referencias/dias-nao-uteis')).data,
  })
}

/** @returns a mutação que cadastra um dia não útil */
export function useCadastrarDia() {
  return useMutacaoDeReferencia((corpo: { data: string; rotulo: string; abrangencia: string }) =>
    api.post('/admin/referencias/dias-nao-uteis', corpo),
  )
}

/** @returns a mutação que remove um dia do calendário */
export function useRemoverDia() {
  return useMutacaoDeReferencia((data: string) => api.delete(`/admin/referencias/dias-nao-uteis/${data}`))
}

/**
 * Marca um dia como conferido contra o calendário do e-Niterói.
 *
 * O endpoint existe desde que a tela existe; o botão é que faltava. Enquanto faltou, os 32 dias do
 * calendário ficaram todos com `conferido = false`, e o TIS não apura em competência nenhuma —
 * `ChamadoConsultaService` devolve `CriterioDoTis.indisponivel` quando há dia por conferir.
 */
export function useConferirDia() {
  return useMutacaoDeReferencia((data: string) =>
    api.patch(`/admin/referencias/dias-nao-uteis/${data}`),
  )
}

/**
 * O que todas as mutações desta tela têm em comum: mandam e derrubam o cache das referências.
 *
 * Inclui `['referencias']` (as listas de ESCOLHA, consumidas pelo assistente de nova solicitação)
 * além de `['admin','referencias']`: sem isso, o sistema recém-cadastrado não apareceria no
 * formulário até um F5 — e quem cadastrou concluiria que o cadastro não funcionou.
 */
function useMutacaoDeReferencia<T>(chamar: (corpo: T) => Promise<unknown>) {
  const cliente = useQueryClient()
  return useMutation({
    mutationFn: async (corpo: T) => {
      await chamar(corpo)
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chaves.tudo() })
      void cliente.invalidateQueries({ queryKey: ['referencias'] })
    },
  })
}
